import React, { useState } from "react";
import {
    Box, Typography, Stack, Switch, Chip, Button,
} from "@mui/material";
import { FilterAlt, CalendarViewWeek, FlashOn, AccessTime, AddCircleOutline } from "@mui/icons-material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs, { Dayjs } from "dayjs";
import { ScheduledRestriction, WEEK_DAYS as ALL_WEEK_DAYS, MONTHS_UI as ALL_MONTHS_UI } from "./TimerConfigurationUtils";

interface RestrictionsConfigurationProps {
    useMonthFilter: boolean;
    setUseMonthFilter: (val: boolean) => void;
    selectedMonths: number[];
    toggleMonth: (id: number) => void;
    useDayFilter: boolean;
    setUseDayFilter: (val: boolean) => void;
    selectedDays: number[];
    toggleDay: (value: number) => void;
    useDayOfMonthFilter: boolean;
    setUseDayOfMonthFilter: (val: boolean) => void;
    selectedDaysOfMonth: number[];
    toggleDayOfMonth: (day: number) => void;
    useFrequency: boolean;
    setUseFrequency: (val: boolean) => void;
    primaryColor: string;
    primaryLightColor: string;
    secondaryColor: string;
    panelBg: string;
    borderColor: string;
    stepDotStyle: (isActive: boolean) => React.CSSProperties;
    MONTHS_UI: { id: number; name: string }[];
    WEEK_DAYS: { id: string; label: string; value: number; name: string }[];
    getTranslation: (key: string) => string;
    // ── Chip-based scheduling ─────────────────────────────────────────────────
    restrictionTime: string;
    setRestrictionTime: (val: string) => void;
    scheduledRestrictions: ScheduledRestriction[];
    onConfirmRestriction: () => void;
    onDeleteRestriction: (id: string) => void;
}

/** Build a short human-readable label for a ScheduledRestriction chip. */
function chipLabel(
    restriction: ScheduledRestriction,
    getTranslation: (key: string) => string
): string {
    const dayLabels = restriction.days.map((v) => {
        const wd = ALL_WEEK_DAYS.find((d) => d.value === v);
        if (!wd) return String(v);
        // Use first 3 chars of translated name
        const translated = getTranslation(
            `pm.propertyeditor.specificproperties.repetition_details.label_weeks.in_the_days.dayoftheweek.${wd.name}`
        );
        return translated ? translated.slice(0, 3) : wd.full.slice(0, 3);
    });

    const monthLabels = restriction.months.map((v) => {
        const mu = ALL_MONTHS_UI.find((m) => m.id === v);
        if (!mu) return String(v);
        const translated = getTranslation(
            `pm.propertyeditor.specificproperties.repetition_details.cycle.label.monthly.${mu.name}`
        );
        return translated ? translated.slice(0, 3) : mu.label;
    });

    const domLabels = restriction.daysOfMonth.slice().sort((a, b) => a - b).map(String);

    const parts: string[] = [];
    if (dayLabels.length) parts.push(dayLabels.join(", "));
    if (monthLabels.length) parts.push(monthLabels.join(", "));
    if (domLabels.length) {
        const dayLabel = getTranslation("generic.day.caption") || "Giorno";
        parts.push(`${dayLabel} ${domLabels.join(",")}`);
    }

    let timeLabel = `⏰ ${restriction.time}`;
    if (restriction.useFrequency && restriction.baseUnit) {
        let unitLabel = "";
        if (restriction.baseUnit === "minutes") unitLabel = getTranslation("generic.minutes.caption") || "minuti";
        else if (restriction.baseUnit === "hours") unitLabel = getTranslation("generic.hours.caption") || "ore";
        else if (restriction.baseUnit === "days") unitLabel = getTranslation("generic.days.caption") || "giorni";
        else if (restriction.baseUnit === "weeks") unitLabel = getTranslation("pm.propertyeditor.specificproperties.repetition_details.duration.label.weeks") || "settimane";
        else if (restriction.baseUnit === "months") unitLabel = getTranslation("generic.months.caption") || "mesi";

        const periodStr = `${getTranslation("pm.timer_config.label.repeat_every") || "Ogni"} ${restriction.baseInterval} ${unitLabel}`;
        timeLabel += ` (${periodStr})`;
    }

    parts.push(timeLabel);
    return parts.join(" | ");
}

const RestrictionsConfiguration: React.FC<RestrictionsConfigurationProps> = ({
    useMonthFilter,
    setUseMonthFilter,
    selectedMonths,
    toggleMonth,
    useDayFilter,
    setUseDayFilter,
    selectedDays,
    toggleDay,
    useDayOfMonthFilter,
    setUseDayOfMonthFilter,
    selectedDaysOfMonth,
    toggleDayOfMonth,
    useFrequency,
    setUseFrequency,
    primaryColor,
    primaryLightColor,
    secondaryColor,
    panelBg,
    borderColor,
    stepDotStyle,
    MONTHS_UI,
    WEEK_DAYS,
    getTranslation,
    restrictionTime,
    setRestrictionTime,
    scheduledRestrictions,
    onConfirmRestriction,
    onDeleteRestriction,
}) => {
    // Whether any filter is active (to show the time picker + confirm)
    const hasAnyFilter = useDayFilter || useDayOfMonthFilter || useMonthFilter || useFrequency;

    const switchSx = {
        "& .MuiSwitch-switchBase.Mui-checked": { color: primaryColor },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: primaryColor },
    };

    const dayButtonSx = (selected: boolean) => ({
        py: 0.5,
        px: 0.5,
        height: 35,
        borderRadius: "12px",
        fontSize: "0.70rem",
        fontWeight: 700,
        border: `1px solid ${selected ? primaryColor : borderColor}`,
        backgroundColor: selected ? primaryLightColor : "background.paper",
        color: selected ? secondaryColor : "text.secondary",
        cursor: "pointer",
        transition: "all 0.15s",
        "&:hover": { borderColor: primaryColor },
    });

    const circleButtonSx = (selected: boolean) => ({
        width: 32,
        height: 32,
        borderRadius: "50%",
        fontSize: "0.70rem",
        fontWeight: 700,
        border: `1px solid ${selected ? primaryColor : borderColor}`,
        backgroundColor: selected ? primaryColor : "background.paper",
        color: selected ? "#fff" : "text.secondary",
        cursor: "pointer",
        transition: "all 0.15s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        "&:hover": { borderColor: primaryColor },
    });

    return (
        <Box
            sx={{
                position: "relative",
                borderLeft: `2px solid ${borderColor}`,
                pl: 5,
                pb: 3,
            }}
        >
            <Box sx={stepDotStyle(false)} />
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
            >
                <Typography
                    variant="caption"
                    fontWeight={700}
                    color={secondaryColor}
                    sx={{ letterSpacing: 1 }}
                >
                    {getTranslation("pm.timer_config.section.restrictions")}
                </Typography>
                <Chip
                    label={getTranslation("pm.timer_config.chip.combinable")}
                    size="small"
                    sx={{
                        fontSize: "0.75rem",
                        height: 24,
                        backgroundColor: "action.hover",
                        color: secondaryColor,
                    }}
                />
            </Stack>

            <Stack spacing={3}>
                {/* ── MONTH FILTER ── */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        border: `1px solid ${useMonthFilter ? primaryColor : borderColor}`,
                        backgroundColor: useMonthFilter ? "background.paper" : panelBg,
                        opacity: useMonthFilter ? 1 : 0.85,
                        transition: "all 0.2s",
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: useMonthFilter ? 1.5 : 0 }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FilterAlt sx={{ fontSize: 16, color: secondaryColor }} />
                            <Typography variant="body2" fontWeight={400} color={secondaryColor}>
                                {getTranslation("pm.timer_config.label.limit_months")}
                            </Typography>
                        </Stack>
                        <Switch
                            size="small"
                            checked={useMonthFilter}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                if (newValue) {
                                    setUseMonthFilter(true);
                                    if (!useDayFilter && !useDayOfMonthFilter && !useFrequency) {
                                        // Mesi non può essere selezionato da solo, ne accendiamo un altro "di default"
                                        setUseDayOfMonthFilter(true);
                                    }
                                } else {
                                    setUseMonthFilter(false);
                                }
                            }}
                            sx={switchSx}
                        />
                    </Stack>

                    {useMonthFilter && (
                        <Box
                            className="timer-panel-fade-in"
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(6, 1fr)",
                                gap: 2,
                            }}
                        >
                            {MONTHS_UI.map((month) => (
                                <Box
                                    key={month.id}
                                    component="button"
                                    onClick={() => toggleMonth(month.id)}
                                    sx={dayButtonSx(selectedMonths.includes(month.id))}
                                >
                                    {getTranslation(
                                        `pm.propertyeditor.specificproperties.repetition_details.cycle.label.monthly.${month.name}`
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* ── DAY OF WEEK FILTER ── */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        border: `1px solid ${useDayFilter ? primaryColor : borderColor}`,
                        backgroundColor: useDayFilter ? "background.paper" : panelBg,
                        opacity: useDayFilter ? 1 : 0.85,
                        transition: "all 0.2s",
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: useDayFilter ? 1.5 : 0 }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarViewWeek sx={{ fontSize: 16, color: secondaryColor }} />
                            <Typography variant="body2" fontWeight={400} color={secondaryColor}>
                                {getTranslation("pm.timer_config.label.limit_weekdays")}
                            </Typography>
                        </Stack>
                        <Switch
                            size="small"
                            checked={useDayFilter}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                if (newValue) {
                                    setUseDayFilter(true);
                                } else {
                                    setUseDayFilter(false);
                                    // Se spegnendo questo lasciamo 'Mesi' da solo (e senza frequenza), riaccendiamo DOM
                                    if (useMonthFilter && !useDayOfMonthFilter && !useFrequency) {
                                        setUseDayOfMonthFilter(true);
                                    }
                                }
                            }}
                            sx={switchSx}
                        />
                    </Stack>

                    {useDayFilter && (
                        <Box
                            className="timer-panel-fade-in"
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
                                gap: 2,
                            }}
                        >
                            {WEEK_DAYS.map((day) => (
                                <Box
                                    key={day.id}
                                    component="button"
                                    onClick={() => toggleDay(day.value)}
                                    sx={dayButtonSx(selectedDays.includes(day.value))}
                                >
                                    {getTranslation(
                                        `pm.propertyeditor.specificproperties.repetition_details.label_weeks.in_the_days.dayoftheweek.${day.name}`
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* ── DAY OF MONTH FILTER ── */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        border: `1px solid ${useDayOfMonthFilter ? primaryColor : borderColor}`,
                        backgroundColor: useDayOfMonthFilter ? "background.paper" : panelBg,
                        opacity: useDayOfMonthFilter ? 1 : 0.85,
                        transition: "all 0.2s",
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: useDayOfMonthFilter ? 1.5 : 0 }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FlashOn sx={{ fontSize: 16, color: secondaryColor }} />
                            <Typography variant="body2" fontWeight={400} color={secondaryColor}>
                                {getTranslation("pm.timer_config.label.limit_monthdays")}
                            </Typography>
                        </Stack>
                        <Switch
                            size="small"
                            checked={useDayOfMonthFilter}
                            onChange={(e) => {
                                const newValue = e.target.checked;
                                if (newValue) {
                                    setUseDayOfMonthFilter(true);
                                } else {
                                    setUseDayOfMonthFilter(false);
                                    // Se spegnendo questo lasciamo 'Mesi' da solo (e senza frequenza), riaccendiamo Day
                                    if (useMonthFilter && !useDayFilter && !useFrequency) {
                                        setUseDayFilter(true);
                                    }
                                }
                            }}
                            sx={switchSx}
                        />
                    </Stack>

                    {useDayOfMonthFilter && (
                        <Box
                            className="timer-panel-fade-in"
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                gap: 1,
                            }}
                        >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                <Box
                                    key={day}
                                    component="button"
                                    onClick={() => toggleDayOfMonth(day)}
                                    sx={circleButtonSx(selectedDaysOfMonth.includes(day))}
                                >
                                    {day}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* ── TIME PICKER + CONFIRM (visible when any filter is active) ── */}
                {hasAnyFilter && (
                    <Box
                        className="timer-panel-fade-in"
                        sx={{
                            p: 2,
                            borderRadius: "12px",
                            border: `1.5px dashed ${primaryColor}`,
                            backgroundColor: primaryLightColor,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AccessTime sx={{ fontSize: 16, color: primaryColor }} />
                            <Typography variant="body2" fontWeight={700} color={secondaryColor}>
                                {getTranslation("timer.daily_start")}
                            </Typography>
                        </Stack>

                        <TimePicker
                            value={dayjs(`2000-01-01T${restrictionTime}`) as Dayjs}
                            onChange={(val: Dayjs | null) => {
                                if (val && val.isValid()) {
                                    setRestrictionTime(val.format("HH:mm"));
                                }
                            }}
                            viewRenderers={{
                                hours: renderTimeViewClock,
                                minutes: renderTimeViewClock,
                                seconds: renderTimeViewClock,
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                    sx: {
                                        backgroundColor: "background.paper",
                                        borderRadius: "8px",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "8px",
                                            "& fieldset": { borderColor: primaryColor },
                                            "&:hover fieldset": { borderColor: primaryColor },
                                            "&.Mui-focused fieldset": {
                                                borderColor: primaryColor,
                                                borderWidth: 2,
                                            },
                                        },
                                    },
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddCircleOutline />}
                            onClick={onConfirmRestriction}
                            disabled={scheduledRestrictions.length >= 1}
                            sx={{
                                borderRadius: "8px",
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                textTransform: "none",
                                backgroundColor: primaryColor,
                                boxShadow: "0 2px 8px rgba(84,189,193,0.35)",
                                "&:hover": {
                                    backgroundColor: "#3da9ad",
                                    boxShadow: "0 4px 12px rgba(84,189,193,0.45)",
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: "action.disabledBackground",
                                    color: "text.disabled",
                                    boxShadow: "none"
                                }
                            }}
                        >
                            {getTranslation("pm.timer_config.button.confirm_restriction") || "Conferma impostazioni timer"}
                        </Button>

                        {/* {scheduledRestrictions.length >= 1 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#e11d48",
                                    fontWeight: 600,
                                    textAlign: "center",
                                    backgroundColor: "#fff1f2",
                                    p: 1,
                                    borderRadius: "6px",
                                    border: "1px solid #fda4af"
                                }}
                            >
                                {getTranslation("pm.timer_config.error.max_chips_reached") || "Al momento è possibile inserire una sola restrizione per timer (limitazione temporanea del sistema)."}
                            </Typography>
                        )} */}
                    </Box>
                )}

                {/* ── CONFIRMED CHIPS AREA ── */}
                {scheduledRestrictions.length > 0 && (
                    <Box className="timer-panel-fade-in">
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            color={secondaryColor}
                            sx={{ letterSpacing: 0.8, mb: 1, display: "block" }}
                        >
                            {getTranslation("pm.timer_config.label.confirmed_restrictions") || "Restrizioni confermate"}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {scheduledRestrictions.map((r) => (
                                <Chip
                                    key={r.id}
                                    label={chipLabel(r, getTranslation)}
                                    onDelete={() => onDeleteRestriction(r.id)}
                                    size="small"
                                    sx={{
                                        backgroundColor: primaryColor,
                                        color: "#fff",
                                        fontWeight: 400,
                                        fontSize: "0.90rem",
                                        borderRadius: "8px",
                                        height: 36,
                                        boxShadow: "0 2px 6px rgba(84,189,193,0.3)",
                                        "& .MuiChip-deleteIcon": {
                                            color: "rgba(255,255,255,0.8)",
                                            "&:hover": { color: "#fff" },
                                        },
                                    }}
                                />
                            ))}
                        </Stack>

                        {/* <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ display: "block", mt: 1.5, fontSize: "0.68rem", fontStyle: "italic", lineHeight: 1.3 }}
                        >
                            {getTranslation("pm.timer_config.info.chips_explanation") || "Puoi concatenare più esecuzioni nello stesso timer creando diversi chip (es. 'Lunedì alle 09:00' e 'Ogni 2 ore il 19 del mese'). Le esecuzioni verranno pianificate in modo indipendente tra loro, garantendo che ogni condizione configurata si attivi all'orario previsto."}
                        </Typography> */}
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

export default RestrictionsConfiguration;
